import React from 'react';
import type { DynamicField } from '../core/types/DynamicField.types';
import { DynamicFieldType } from '../core/types/DynamicField.types';
import { Input } from '../../Input/Input';
import { Select } from '../../Select/Select';
import { Textarea } from '../../Textarea/Textarea';
import { Checkbox } from '../../Checkbox/Checkbox';
import { Switch } from '../../Switch/Switch';
import { RadioGroup, RadioGroupItem } from '../../Radio/Radio';
import { LookupField } from '../../LookupField';
import { useOptionalDynamicEntitiesContext, useDynamicEntitiesFetcher } from '../core/DynamicEntitiesProvider';

/**
 * Interpreta como booleano lo que llega del backend para un Checkbox o un Switch.
 *
 * 🔴 Antes era `!!value`, y por eso el control se veía ENCENDIDO con el valor `false`: la API
 * entrega el booleano ya convertido a cadena —`"False"` con mayúscula, que es el `bool.ToString()`
 * de .NET— y en JavaScript toda cadena no vacía es truthy. `!!"False" === true`. El dato viajaba
 * bien de punta a punta; lo único que mentía era el pintado.
 *
 * El `onChange` de ambos controles SÍ emite un booleano de verdad, así que el componente vive en dos
 * formatos: emite `boolean`, recibe `string`. Se normaliza al leer, que es el lado roto.
 */
const asBoolean = (value: unknown): boolean => {
    if (typeof value === 'boolean') return value;
    if (typeof value === 'string') {
        const normalized = value.trim().toLowerCase();
        // '' y cualquier texto que no sea afirmativo cuentan como apagado: un control de dos
        // estados no puede quedarse sin estado.
        return normalized === 'true' || normalized === '1';
    }
    if (typeof value === 'number') return value !== 0;
    return false;
};

// We need a way to resolved generic lists. 
// For now, let's assume options are passed or we have a hook to fetching them.
// The Definition has "lists", so the parent Grid/Renderer should pass the list options.

interface DynamicFieldRendererProps {
    field: DynamicField;
    value: any;
    onChange: (value: any) => void;
    error?: string;
    options?: { label: string; value: string | number }[]; // For Select/Radio
}

export const DynamicFieldRenderer: React.FC<DynamicFieldRendererProps> = ({
    field,
    value,
    onChange,
    error,
    options = []
}) => {
    const ctx = useOptionalDynamicEntitiesContext();
    // baseUrl ya termina en /api; la API v1 cuelga del mismo prefijo (${baseUrl}/v1/...).
    // No extraemos el origin para soportar baseUrl relativo (modo MFE detrás del shell).
    const baseUrl = ctx?.baseUrl ?? '';
    const http = useDynamicEntitiesFetcher();

    const commonProps = {
        id: field.code,
        label: field.labels['es'] || field.code, // Fallback to ES or code
        disabled: field.disabled || field.readonly,
        required: field.required,
        error: !!error,
    };

    switch (field.type) {
        case DynamicFieldType.Text:
            return (
                <Input
                    {...commonProps}
                    inputSize="sm"
                    value={value || ''}
                    onChange={(e) => onChange(e.target.value)}
                />
            );

        case DynamicFieldType.TextArea:
            return (
                <Textarea
                    {...commonProps}
                    value={value || ''}
                    onChange={(e) => onChange(e.target.value)}
                />
            );

        case DynamicFieldType.NumberInteger:
        case DynamicFieldType.NumberDecimal:
        case DynamicFieldType.Currency:
            return (
                <Input
                    {...commonProps}
                    type="number"
                    inputSize="sm"
                    value={value || ''}
                    onChange={(e) => onChange(e.target.value)}
                />
            );

        case DynamicFieldType.Date:
            // Input type="date" expects strictly "YYYY-MM-DD"
            const dateValue = value ? new Date(value).toISOString().split('T')[0] : '';
            return (
                <Input
                    {...commonProps}
                    type="date"
                    inputSize="sm"
                    value={dateValue}
                    onChange={(e) => onChange(e.target.value)}
                />
            );

        case DynamicFieldType.Time:
            // Input type="time" expects "HH:mm" or "HH:mm:ss"
            const timeValue = value && value.includes('T')
                ? value.split('T')[1].slice(0, 5)
                : (value?.slice(0, 5) || '');
            return (
                <Input
                    {...commonProps}
                    type="time"
                    inputSize="sm"
                    value={timeValue}
                    onChange={(e) => onChange(e.target.value)}
                />
            );

        case DynamicFieldType.DateTime:
            // Input type="datetime-local" expects "YYYY-MM-DDTHH:mm"
            const dateTimeValue = value ? new Date(value).toISOString().slice(0, 16) : '';
            return (
                <Input
                    {...commonProps}
                    type="datetime-local"
                    inputSize="sm"
                    value={dateTimeValue}
                    onChange={(e) => onChange(e.target.value)}
                />
            );

        case DynamicFieldType.Select:
        case DynamicFieldType.Multiselect: // Added Multiselect support
            return (
                <Select
                    {...commonProps}
                    options={options}
                    value={value}
                    onChange={(val) => onChange(val)}
                    placeholder={options.length === 0 ? "Sin opciones disponibles" : "Seleccionar..."}
                    disabled={commonProps.disabled || options.length === 0}
                    selectSize="sm"
                />
            );

        case DynamicFieldType.Checkbox:
            const { error: cbError, ...cbProps } = commonProps;
            return (
                <div className="mb-4">
                    <Checkbox
                        {...cbProps}
                        checked={asBoolean(value)}
                        onChange={(e) => onChange((e.target as HTMLInputElement).checked)}
                    />
                    {cbError && error && (
                        <p className="text-sm leading-5 text-error-content mt-1">{error}</p>
                    )}
                </div>
            );

        case DynamicFieldType.Switch:
            const { error: swError, ...swProps } = commonProps;
            return (
                <div className="mb-4">
                    <Switch
                        {...swProps}
                        checked={asBoolean(value)}
                        onCheckedChange={(checked) => onChange(checked)}
                    />
                    {swError && error && (
                        <p className="text-sm leading-5 text-error-content mt-1">{error}</p>
                    )}
                </div>
            );

        case DynamicFieldType.Radio:
            const { error: rdError, ...rdProps } = commonProps;
            return (
                <div className="mb-4">
                    <label className="font-bold text-sm leading-5 text-content-primary mb-2 block">
                        {commonProps.label}
                    </label>
                    {options.length > 0 ? (
                        <RadioGroup
                            value={value !== undefined && value !== null ? String(value) : undefined}
                            onValueChange={(val) => onChange(val)}
                            disabled={rdProps.disabled}
                            className="flex flex-wrap gap-4"
                        >
                            {options.map(opt => (
                                <RadioGroupItem
                                    key={String(opt.value)}
                                    value={String(opt.value)}
                                    label={opt.label}
                                    id={`${field.code}-${opt.value}`}
                                />
                            ))}
                        </RadioGroup>
                    ) : (
                        <div className="text-sm text-gray-400 italic bg-gray-50 p-2 rounded border border-dashed">
                            Sin opciones configuradas (asociar catálogo)
                        </div>
                    )}
                    {rdError && error && (
                        <p className="text-sm leading-5 text-error-content mt-1">{error}</p>
                    )}
                </div>
            );

        case DynamicFieldType.GenericEntity:
            const { error: geError, ...geProps } = commonProps;
            // Since field.genericEntityId points to the entity definition ID, we need to pass it
            // Wait, we only have the genericEntityId, not the CODE. So we must use a search that filters by GenericEntityID.
            // Oh right, the /items/search endpoint searches across all items. We MUST add a filter for the specific GenericEntityID!
            return (
                <div className="mb-4">
                    {field.genericEntityId ? (
                        <LookupField
                            {...geProps}
                            entity="generic-entities/items"
                            displayFields={['Code', 'Value']}
                            value={value || null}
                            onChange={(record) => onChange(record?.Id || null)}
                            fetcher={async (entity, request) => {
                                // FilterExpression semántica: outer = AND, inner = OR.
                                // Cada condición forzada va en su PROPIO grupo externo para
                                // garantizar AND entre ellas. Ponerlas en un mismo inner array
                                // las convertiría en OR, lo que permitiría que IsDeleted=false
                                // o IsActive=true devuelva ítems de cualquier entidad.
                                const baseFilters = [
                                    [{ GenericEntityID: field.genericEntityId }], // AND: solo esta entidad
                                    [{ IsDeleted: false }],                        // AND: no eliminados
                                    [{ IsActive: true }],                          // AND: solo activos
                                ];

                                // Mapear 'Id' → 'ID' en los filtros que trae el LookupField
                                const mappedRequestFilters = (request.filters ?? []).map(group =>
                                    group.map(cond => {
                                        const mappedCond: any = {};
                                        for (const key in cond) {
                                            mappedCond[key === 'Id' ? 'ID' : key] = cond[key as keyof typeof cond];
                                        }
                                        return mappedCond;
                                    })
                                );

                                const mappedRequest = {
                                    ...request,
                                    fields: request.fields.filter(f => f !== 'Id').concat('ID'),
                                    searchFields: request.searchFields?.map(f => f === 'Id' ? 'ID' : f),
                                    filters: [...baseFilters, ...mappedRequestFilters]
                                };

                                const res = await http(`${baseUrl}/v1/generic-entities/items/search`, {
                                    method: 'POST',
                                    headers: { 'Content-Type': 'application/json' },
                                    body: JSON.stringify(mappedRequest)
                                });

                                if (!res.ok) throw new Error('Error fetching generic entity items');
                                const data = await res.json();

                                // 2. Map `ID` back to `Id` for LookupField compatibility
                                return {
                                    ...data,
                                    data: data.data.map((item: any) => ({
                                        ...item,
                                        Id: item.ID
                                    }))
                                };
                            }}
                        />
                    ) : (
                        <div className="text-sm text-orange-600 bg-orange-50 p-2 rounded border border-orange-200">
                            ⚠️ Este campo no tiene una Entidad Genérica asignada en su configuración.
                        </div>
                    )}
                    {geError && error && (
                        <p className="text-sm leading-5 text-error-content mt-1">{error}</p>
                    )}
                </div>
            );

        default:
            return <div className="text-gray-400">Tipo de campo no soportado: {field.type}</div>;
    }
};
